import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { vehicles as vApi, inspections, files, formatErr } from "@/lib/api";
import { Camera, Signature as SignatureIcon, Plus, X, CarProfile, CheckCircle } from "@phosphor-icons/react";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";
import ConfirmDelete from "@/components/ConfirmDelete";
import { AddedBy } from "@/components/DriverFilter";
import { money } from "@/components/LoadMoney";
import PdfButtons from "@/components/PdfButtons";

const REQUIRED_SHOTS = [
  { key: "front", label: "Front" }, { key: "driver", label: "Driver side" },
  { key: "rear", label: "Rear" }, { key: "passenger", label: "Passenger side" },
  { key: "vin", label: "VIN plate" }, { key: "odo", label: "Odometer / dash" },
];
const DAMAGE_CODES = ["scratch","dent","chip","crack","broken","missing","paint","wheel","glass","other"];
const AREAS = ["front","rear","driver_front","driver_rear","passenger_front","passenger_rear","roof","glass","wheels","interior"];

export function VehicleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [v, setV] = useState(null);
  useEffect(() => { vApi.get(id).then(setV); }, [id]);
  if (!v) return <AppShell>Loading…</AppShell>;
  const pickup = v.inspections?.find(i => i.type === "pickup");
  const delivery = v.inspections?.find(i => i.type === "delivery");

  return <AppShell>
    <div className="flex items-center justify-between mb-6">
      <div>
        <Link to={`/app/loads/${v.load_id}`} className="eyebrow text-accent hover:underline">← Back to load</Link>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">
          <CarProfile className="inline mr-2" size={32} weight="duotone"/>
          {v.year} {v.make} {v.model}
        </h1>
        <div className="font-mono-tech text-xs text-muted-foreground mt-1">{v.vin}</div>
      </div>
      <span className="eyebrow text-accent">{v.status?.replace("_"," ")}</span>
    </div>

    <div className="grid md:grid-cols-3 gap-3 mb-6">
      <Info k="Color" v={v.color}/>
      <Info k="Odometer" v={v.odometer}/>
      <Info k="Stock / Lot" v={`${v.stock_number || "—"} / ${v.auction_lot || "—"}`}/>
      <Info k="Pickup address" v={v.pickup_address}/>
      <Info k="Delivery address" v={v.delivery_address}/>
      <Info k="Load" v={`#${v.load?.load_number || "—"}`}/>
      {v.added_by && <Info k="Vehicle added by" v={<span data-testid="vehicle-detail-added-by">{v.added_by}</span>}/>}
      {v.rate != null && <Info k="Rate" v={<span data-testid="vehicle-detail-rate">{money(v.rate)}</span>}/>}
    </div>

    <div className="grid md:grid-cols-2 gap-4 mb-6">
      <InspectionCard title="Pickup inspection" insp={pickup} action={!pickup && (
        <Link to={`/app/vehicles/${id}/inspection/pickup`} data-testid="btn-start-pickup" className="btn-amber"><Camera size={16} weight="bold"/> Start pickup</Link>
      )}/>
      <InspectionCard title="Delivery inspection" insp={delivery} action={!delivery && (
        <Link to={`/app/vehicles/${id}/inspection/delivery`} data-testid="btn-start-delivery" className="btn-amber" aria-disabled={!pickup}><Camera size={16} weight="bold"/> Start delivery</Link>
      )}/>
    </div>

    <div className="flex flex-wrap gap-3">
      <PdfButtons vehicle={v}/>
      <ConfirmDelete testid="btn-delete-vehicle" label="Delete vehicle"
        title={`Delete ${v.year || ""} ${v.make || ""} ${v.model || ""}?`}
        description={`This permanently removes VIN ${v.vin}${v.inspections?.length ? ` and its ${v.inspections.length} inspection(s), photos and signatures` : ""} from load #${v.load?.load_number || ""}. This can't be undone.`}
        onConfirm={async () => {
          try { await vApi.del(id); localStorage.removeItem(`hg-draft-${id}-pickup`); localStorage.removeItem(`hg-draft-${id}-delivery`); toast.success("Vehicle deleted"); nav(`/app/loads/${v.load_id}`); }
          catch (e) { toast.error(formatErr(e)); }
        }}/>
    </div>
  </AppShell>;
}

function InspectionCard({ title, insp, action }) {
  return <div className="bg-white border border-border/60 p-5">
    <div className="flex items-center justify-between mb-2">
      <h3 className="font-display font-bold text-lg">{title}</h3>
      {insp ? <CheckCircle size={20} className="text-green-600" weight="fill"/> : <span className="eyebrow text-muted-foreground">pending</span>}
    </div>
    {insp ? (
      <div className="text-sm space-y-1">
        <div className="text-muted-foreground">{new Date(insp.created_at).toLocaleString()}</div>
        <div>{insp.address || "—"}</div>
        {insp.odometer != null && <div className="font-mono-tech text-xs">Odometer: {insp.odometer}</div>}
        <div>{insp.notes}</div>
        <div className="eyebrow text-accent mt-2">{insp.photo_ids?.length || 0} photos · {insp.damages?.length || 0} damage marks</div>
        {insp.photo_ids?.length > 0 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar mt-2">
            {insp.photo_ids.slice(0,8).map(fid => (
              <img key={fid} src={files.url(fid)} alt="" className="w-20 h-20 object-cover border border-border/60"/>
            ))}
          </div>
        )}
        {insp.signer_name && <div className="mt-2 text-xs">Signed by: <b>{insp.signer_name}</b></div>}
        <div className="mt-1"><AddedBy name={insp.added_by} tid={`inspection-added-by-${insp.type}`}/></div>
      </div>
    ) : action}
  </div>;
}

export function InspectionFlow() {
  const { id, type } = useParams();
  const nav = useNavigate();
  const draftKey = `hg-draft-${id}-${type}`;
  const [draft] = useState(() => { try { return JSON.parse(localStorage.getItem(draftKey)) || {}; } catch { return {}; } });
  const [v, setV] = useState(null);
  const [step, setStep] = useState(draft.step || 0);
  const [photos, setPhotos] = useState(draft.photos || {}); // key -> fileId
  const [extraPhotos, setExtraPhotos] = useState(draft.extraPhotos || []); // file ids
  const [damages, setDamages] = useState(draft.damages || []);
  const [notes, setNotes] = useState(draft.notes || "");
  const [odo, setOdo] = useState(draft.odo || "");
  const [signerName, setSignerName] = useState(draft.signerName || "");
  const [signatureId, setSignatureId] = useState(draft.signatureId || null);
  const [gps, setGps] = useState({ lat: null, lng: null });
  const [uploading, setUploading] = useState(false);
  const sigRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(draftKey, JSON.stringify({ step, photos, extraPhotos, damages, notes, odo, signerName, signatureId }));
  }, [draftKey, step, photos, extraPhotos, damages, notes, odo, signerName, signatureId]);
  useEffect(() => {
    if (step !== 3 || !sigRef.current) return;
    const c = sigRef.current.getCanvas();
    c.width = c.offsetWidth; c.height = c.offsetHeight;
  }, [step]);

  useEffect(() => { vApi.get(id).then(setV); }, [id]);
  useEffect(() => {
    if (navigator.geolocation) navigator.geolocation.getCurrentPosition(
      pos => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }), () => {}
    );
  }, []);

  const uploadFile = async (file, kind="photo") => {
    setUploading(true);
    try { const r = await files.upload(file, kind); return r.id; }
    catch (e) { toast.error(`Upload failed: ${formatErr(e)}`); return null; }
    finally { setUploading(false); }
  };
  const onPickShot = async (key, file) => {
    const fid = await uploadFile(file, "photo");
    if (!fid) return;
    setPhotos(p => ({ ...p, [key]: fid }));
    toast.success(`${key} captured`);
  };
  const onPickExtra = async (file) => {
    const fid = await uploadFile(file, "photo");
    if (fid) setExtraPhotos(p => [...p, fid]);
  };

  const addDamage = () => setDamages(d => [...d, { code: "scratch", area: "front", severity: "minor", description: "", photo_ids: [] }]);
  const updateDamage = (i, patch) => setDamages(d => d.map((x,ix) => ix===i ? {...x,...patch}:x));
  const removeDamage = (i) => setDamages(d => d.filter((_,ix) => ix!==i));
  const addDamagePhoto = async (i, file) => {
    const fid = await uploadFile(file, "damage");
    if (fid) setDamages(d => d.map((x,ix) => ix===i ? {...x, photo_ids: [...(x.photo_ids||[]), fid]} : x));
  };

  const captureSignature = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) { toast.error("Sign first"); return; }
    const blob = await new Promise(res => sigRef.current.getCanvas().toBlob(res, "image/png"));
    if (!blob) { toast.error("Could not read signature, please try again"); return; }
    const fid = await uploadFile(new File([blob], "signature.png", { type: "image/png" }), "signature");
    if (!fid) return;
    setSignatureId(fid);
    toast.success("Signature captured");
  };

  const submit = async () => {
    const photo_ids = [...Object.values(photos), ...extraPhotos];
    try {
      await inspections.create({
        vehicle_id: id, type,
        odometer: odo ? parseInt(odo) : null,
        gps_lat: gps.lat, gps_lng: gps.lng,
        address: type === "pickup" ? v?.pickup_address : v?.delivery_address,
        notes, photo_ids, damages,
        signer_name: signerName || null, signature_id: signatureId,
      });
      toast.success(`${type} inspection saved`);
      localStorage.removeItem(draftKey);
      nav(`/app/vehicles/${id}`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (!v) return <AppShell focus>Loading…</AppShell>;
  const typeTitle = type === "pickup" ? "Pickup inspection" : "Delivery inspection";

  return <AppShell focus>
    <div className="mb-4">
      <Link to={`/app/vehicles/${id}`} className="eyebrow text-accent hover:underline">← Back</Link>
      <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">{typeTitle}</h1>
      <div className="font-mono-tech text-xs text-muted-foreground">{v.vin} · {v.year} {v.make} {v.model}</div>
    </div>

    <StepBar step={step} steps={["Photos","Damage","Notes","Signature"]}/>

    {step === 0 && (
      <div className="space-y-4">
        <div className="eyebrow text-accent">Required shots</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {REQUIRED_SHOTS.map(s => (
            <ShotTile key={s.key} s={s} fid={photos[s.key]} onPick={(f)=>onPickShot(s.key, f)}/>
          ))}
        </div>
        <div className="eyebrow text-accent mt-6">Extra photos ({extraPhotos.length})</div>
        <div className="flex flex-wrap gap-2">
          {extraPhotos.map(fid => <img key={fid} src={files.url(fid)} alt="" className="w-24 h-24 object-cover border border-border/60"/>)}
          <label data-testid="btn-add-extra" className="w-24 h-24 border-2 border-dashed border-border flex items-center justify-center cursor-pointer">
            <Plus size={24}/>
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e)=>e.target.files[0] && onPickExtra(e.target.files[0])}/>
          </label>
        </div>
      </div>
    )}

    {step === 1 && (
      <div className="space-y-3">
        {damages.map((d, i) => (
          <div key={i} className="bg-white border border-border/60 p-4 space-y-2 slide-up">
            <div className="flex items-center justify-between">
              <div className="eyebrow">Damage #{i+1}</div>
              <button onClick={()=>removeDamage(i)} data-testid={`btn-rm-damage-${i}`}><X size={18}/></button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Sel value={d.code} onChange={v=>updateDamage(i,{code:v})} opts={DAMAGE_CODES} tid={`sel-code-${i}`}/>
              <Sel value={d.area} onChange={v=>updateDamage(i,{area:v})} opts={AREAS} tid={`sel-area-${i}`}/>
              <Sel value={d.severity} onChange={v=>updateDamage(i,{severity:v})} opts={["minor","moderate","major"]} tid={`sel-sev-${i}`}/>
            </div>
            <input data-testid={`in-dmg-desc-${i}`} value={d.description} onChange={(e)=>updateDamage(i,{description:e.target.value})}
                   placeholder="Short description" className="w-full border-2 border-border px-2 py-2 text-sm"/>
            <div className="flex flex-wrap gap-2">
              {d.photo_ids?.map(fid => <img key={fid} src={files.url(fid)} alt="" className="w-20 h-20 object-cover border"/>)}
              <label className="w-20 h-20 border-2 border-dashed flex items-center justify-center cursor-pointer">
                <Camera size={20}/>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e)=>e.target.files[0] && addDamagePhoto(i, e.target.files[0])}/>
              </label>
            </div>
          </div>
        ))}
        <button onClick={addDamage} data-testid="btn-add-damage" className="btn-dark w-full"><Plus size={16}/> Add damage</button>
      </div>
    )}

    {step === 2 && (
      <div className="space-y-4">
        <label className="block"><span className="eyebrow mb-1 block">Odometer</span>
          <input data-testid="in-odo" value={odo} onChange={(e)=>setOdo(e.target.value)} type="number" className="w-full border-2 border-border focus:border-accent px-3 py-3"/>
        </label>
        <label className="block"><span className="eyebrow mb-1 block">Notes</span>
          <textarea data-testid="in-notes" value={notes} onChange={(e)=>setNotes(e.target.value)} rows={4} className="w-full border-2 border-border focus:border-accent px-3 py-3"/>
        </label>
        {gps.lat && <div className="text-xs text-muted-foreground font-mono-tech">GPS: {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}</div>}
      </div>
    )}

    {step === 3 && (
      <div className="space-y-4">
        <label className="block"><span className="eyebrow mb-1 block">Signer printed name</span>
          <input data-testid="in-signer" value={signerName} onChange={(e)=>setSignerName(e.target.value)} className="w-full border-2 border-border focus:border-accent px-3 py-3"/>
        </label>
        <div>
          <div className="eyebrow mb-1">Signature</div>
          <SignatureCanvas ref={sigRef} canvasProps={{ className: "sig-pad w-full h-40" }}/>
          <div className="flex gap-2 mt-2">
            <button onClick={()=>{ sigRef.current?.clear(); setSignatureId(null); }} data-testid="btn-sig-clear" className="btn-dark !py-2 !px-3 !text-xs">Clear</button>
            <button onClick={captureSignature} data-testid="btn-sig-capture" className="btn-amber !py-2 !px-3 !text-xs">Capture signature</button>
            {signatureId && <span className="eyebrow text-green-700 flex items-center">✓ Captured</span>}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">The signature acknowledges the recorded condition and documentation for this VIN.</p>
        </div>
      </div>
    )}

    <div className="fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur border-t-2 border-border lg:static lg:bg-transparent lg:border-0 lg:backdrop-blur-none lg:mt-6"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="inspection-action-bar">
      <div className="max-w-7xl mx-auto px-4 lg:px-0 pt-2 pb-3 lg:p-0">
        <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2 lg:hidden" data-testid="inspection-progress">
          <span>Step {step + 1} of 4 · {["Photos", "Damage", "Notes", "Signature"][step]}</span>
          <span className={uploading ? "text-accent" : ""}>
            {uploading ? "Uploading…" : step === 0 ? `${Object.keys(photos).length}/${REQUIRED_SHOTS.length} shots` : step === 3 ? (signatureId ? "Signed ✓" : "Not signed yet") : ""}
          </span>
        </div>
        <div className="flex justify-between gap-2">
          <button onClick={()=>setStep(s=>Math.max(0,s-1))} disabled={step===0 || uploading} data-testid="btn-prev" className="btn-dark flex-1 disabled:opacity-40">Back</button>
          {step < 3
            ? <button onClick={()=>setStep(s=>s+1)} disabled={uploading} data-testid="btn-next" className="btn-amber flex-[2] lg:flex-1 disabled:opacity-60">Next</button>
            : <button onClick={submit} disabled={uploading} data-testid="btn-finish" className="btn-amber flex-[2] lg:flex-1 disabled:opacity-60">Save inspection</button>}
        </div>
      </div>
    </div>
  </AppShell>;
}

function StepBar({ step, steps }) {
  return <div className="flex items-center gap-2 mb-6">
    {steps.map((s,i)=>(
      <div key={i} className="flex-1">
        <div className={`h-1 ${i<=step ? 'bg-accent':'bg-border'}`}/>
        <div className={`mt-1 eyebrow ${i===step?'text-accent':'text-muted-foreground'}`}>{i+1}·{s}</div>
      </div>
    ))}
  </div>;
}
function ShotTile({ s, fid, onPick }) {
  return <label data-testid={`shot-${s.key}`} className={`relative aspect-square border-2 ${fid?'border-green-600':'border-dashed border-border'} flex items-center justify-center cursor-pointer bg-white overflow-hidden`}>
    {fid ? <img src={files.url(fid)} alt={s.label} className="absolute inset-0 w-full h-full object-cover"/>
         : <div className="flex flex-col items-center gap-1 text-muted-foreground"><Camera size={24}/><span className="eyebrow">{s.label}</span></div>}
    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e)=>e.target.files[0] && onPick(e.target.files[0])}/>
  </label>;
}
function Sel({ value, onChange, opts, tid }) {
  return <select data-testid={tid} value={value} onChange={(e)=>onChange(e.target.value)} className="border-2 border-border px-2 py-2 text-sm bg-white">
    {opts.map(o => <option key={o} value={o}>{o.replace("_"," ")}</option>)}
  </select>;
}
function Info({ k, v }) { return <div className="bg-white border border-border/60 p-3"><div className="eyebrow text-muted-foreground">{k}</div><div className="font-display font-bold">{v || "—"}</div></div>; }
